import { useMemo } from 'react'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import useOfferList from 'hooks/Offers/useOfferList'
import {
  LOW_VIEW_DESTINATIONS_SALES_CTR_RANKING_DESTINATION_IDS,
} from 'constants/lowViewDestinationsSalesCtrRankingDestinations'
import { hasTopOffersChanged, HOTEL_OFFER_TYPES, TOP_N } from 'hooks/salesRankingUtils'

interface UseLowViewDestinationsSalesAndCtrRankingResult {
  experimentVariant: string | undefined
  rankingComparisonPending: boolean
}

function useLowViewDestinationsSalesAndCtrRanking(destinationId: string | undefined): UseLowViewDestinationsSalesAndCtrRankingResult {
  const shouldCompare = !!destinationId && LOW_VIEW_DESTINATIONS_SALES_CTR_RANKING_DESTINATION_IDS.has(destinationId)

  const baseFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!shouldCompare) return undefined
    return {
      destinationId,
      offerTypes: HOTEL_OFFER_TYPES,
      limit: TOP_N,
    }
  }, [shouldCompare, destinationId])

  const filtersWithRollout = useMemo((): App.OfferListFilters | undefined => {
    if (!baseFilters) return undefined
    return {
      ...baseFilters,
      rollout: ['salesCtrRanking'],
    }
  }, [baseFilters])

  const withRolloutList = useOfferList(filtersWithRollout, { disabled: !shouldCompare })
  const withoutRolloutList = useOfferList(baseFilters, { disabled: !shouldCompare })

  const rankingComparisonPending = shouldCompare && (withRolloutList.fetching || withoutRolloutList.fetching)

  const rankingsDiffer = useMemo(() => {
    if (!shouldCompare || rankingComparisonPending) return false

    return hasTopOffersChanged(withRolloutList.offerIds, withoutRolloutList.offerIds)
  }, [shouldCompare, rankingComparisonPending, withRolloutList.offerIds, withoutRolloutList.offerIds])

  const canActivate = shouldCompare && !rankingComparisonPending && rankingsDiffer

  const { variation } = useExperiment(
    Experiments.searchLowViewDestinationsSalesCtrRanking,
    { canActivate },
  )
  const experimentVariant = canActivate ? variation : undefined

  return { experimentVariant, rankingComparisonPending }
}

export default useLowViewDestinationsSalesAndCtrRanking
